/**
 * Same-origin API path builder for the /smile chrome's client fetches.
 *
 * The pod is served by the gateway under a "/bb" segment: the public URL is
 * /smile/bb/*, the gateway strips the /smile host-selector, and the pod (and a
 * same-origin browser fetch) reach the route handlers at /bb/api/*. next.config
 * rewrites /:seg/api/:path* back onto the App Router's own /api/:path* handler,
 * so /bb/api/* resolves in every environment (in local dev the same rewrite
 * maps /bb/api/* -> /api/*). The proxy matcher excludes `bb/api` for the same
 * reason. See next.config.ts rewrites() and proxy.ts matcher.
 *
 * Client fetches must therefore hit /bb/api/... and NOT a bare /api/... A bare
 * root-relative /api/... resolves against the origin root
 * (https://wwwstg.aspendental.com/api/...), which the gateway does not route to
 * this pod, so it 404s. That was the TAG-176 (/api/geo/) and TAG-177
 * (/api/facility/collection/) launch break: the nearest-office ladder's two
 * client fetches used bare /api/ paths and 404'd on staging.
 *
 * We stay SAME-ORIGIN on purpose (path only, never config.baseUrl's fully
 * qualified https://wwwstg... URL): a fully qualified base URL would send the
 * request off the current preview/stage door under ENVIRONMENT_NAME=stage,
 * which is exactly what the original bare-path comment was trying to avoid. The
 * fix is the missing /bb prefix, not a cross-origin URL.
 */

export const API_MOUNT_SEGMENT = "/bb";

/**
 * Prefixes a root-relative /api/... path with the same-origin mount segment so
 * the gateway routes it to this pod. `apiPath` must start with "/api/".
 *
 * Idempotent: an already-prefixed path is returned unchanged, so callers and
 * tests can pass either form without double-prefixing.
 */
export function smileApiPath(apiPath: string): string {
  if (apiPath.startsWith(`${API_MOUNT_SEGMENT}/`)) return apiPath;
  return `${API_MOUNT_SEGMENT}${apiPath}`;
}
